import {
  DatabaseAuto,
  IAnalyticsResultExpense,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IcreditNote,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  TcreditNoteRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TcreditNoteProps = IcreditNote<number, IcommerceUser>;

/**
 * Represents a credit note in the system.
 * This class provides methods to manage and track credit notes,
 * which are used to issue refunds or credits to customers.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TcreditNoteProps - The type definition for credit note properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link CreditNote} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const creditNote = await CreditNote.getAll();
 *
 * const creditNote = await CreditNote.filterAll({
 *
 * });
 *
 * const creditNote = await CreditNote.bulkDelete({
 *
 * });
 *
 * const creditNote = await CreditNote.getOne('resourceId');
 *
 * const creditNote = await CreditNote.remove();
 *
 * ```
 */
export class CreditNote extends DatabaseAuto {
  /** Base API endpoint for credit note operations */
  static readonly baseUrl = '/credit-note';

  /** Unique reference ID for the credit note */
  readonly urId: string;

  /** E-commerce vendors associated with the credit note */
  readonly ecommerceVendors: TcreditNoteProps['ecommerceVendors'];

  /** Unique number identifying the credit note */
  readonly creditNoteNumber: TcreditNoteProps['creditNoteNumber'];

  /** Items included in the credit note */
  readonly creditNoteItems: TcreditNoteProps['creditNoteItems'];

  /** Total amount of the credit note */
  readonly amount: number;

  /** Current status of the credit note */
  readonly creditNoteStatus: TcreditNoteProps['creditNoteStatus'];

  /** Reference to the original invoice */
  readonly invoice: TcreditNoteProps['invoice'];

  /** Company that issued the credit note */
  readonly company: TcreditNoteProps['company'];

  /** Date when the credit note was created */
  readonly date: TcreditNoteProps['date'];

  /**
   * Creates a new instance of CreditNote.
   * @param {TcreditNoteProps} data - The credit note data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TcreditNoteProps) {
    super(data);

    this.urId = data.urId;
    this.ecommerceVendors = data.ecommerceVendors;
    this.creditNoteNumber = data.creditNoteNumber;
    this.creditNoteItems = data.creditNoteItems;
    this.amount = data.amount;
    this.creditNoteStatus = data.creditNoteStatus;
    this.invoice = data.invoice;
    this.company = data.company;
    this.date = data.date;
  }

  /**
   * Retrieves analytics for credit notes based on the provided filter criteria.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IAnalyticsResultExpense>} The analytics results
   * @throws {Error} If there's an error fetching the analytics
   */
  static getCreditNoteAnalytics = (filter: IfilterProps) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IAnalyticsResultExpense>(`${CreditNote.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves all credit notes with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: CreditNote[] }>}
   * An object containing the total count and an array of credit notes
   * @throws {Error} If there's an error fetching the credit notes
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TcreditNoteProps>>(`${CreditNote.baseUrl}?${query}`);
    const creditNotes = await lastValueFrom(observer$);

    return {
      count: creditNotes.count,
      dataArr: creditNotes.data.map(val => new CreditNote(val))
    };
  }

  /**
   * Filters credit notes based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: CreditNote[] }>}
   * An object containing the total count and an array of filtered credit notes
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TcreditNoteProps>>(
        `${CreditNote.baseUrl}/filter`,
        filter
      );
    const creditNotes = await lastValueFrom(observer$);

    return {
      count: creditNotes.count,
      dataArr: creditNotes.data.map(val => new CreditNote(val))
    };
  }

  /**
   * Retrieves a single credit note by its ID.
   * @param {string} resourceId - The unique identifier of the credit note to retrieve
   * @returns {Promise<CreditNote>} The requested credit note
   * @throws {Error} If the credit note is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TcreditNoteProps>(`${CreditNote.baseUrl}/${resourceId}`);
    const creditNote = await lastValueFrom(observer$);

    return new CreditNote(creditNote);
  }

  /**
   * Creates a new credit note record.
   * @param {TcreditNoteRequest<true>} payload - The credit note data to create
   * @returns {Promise<CreditNote>} The newly created credit note
   * @throws {Error} If the creation fails
   */
  static async add(payload: TcreditNoteRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TcreditNoteProps>(`${CreditNote.baseUrl}`, payload);

    return new CreditNote(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple credit notes based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which credit notes to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${CreditNote.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current credit note with new values.
   * @param {TcreditNoteRequest} payload - The data to update the credit note with
   * @returns {Promise<CreditNote>} The updated credit note
   * @throws {Error} If the update operation fails
   */
  async update(payload: TcreditNoteRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TcreditNoteProps>(
        `${CreditNote.baseUrl}/${this._id}`,
        payload
      );

    return new CreditNote(await lastValueFrom(observer$));
  }

  /**
   * Removes the current credit note from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the credit note doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${CreditNote.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
